import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Edit2, Save, Sparkles, Trash2, X } from 'lucide-react';
import type { CollectionKey } from '../../../shared/types';
import type { ItemField } from '../../types/ui';
import type { UpdateItem, DeleteItem } from '../../types/actions';
import AutoResizeTextarea from '../ui/AutoResizeTextarea';
import MarkdownContent from '../ui/MarkdownContent';
import AnimatedCardPortal from '../ui/AnimatedCardPortal';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ui/ConfirmDialog';

export default function ItemCard({
  item,
  category,
  fields,
  updateItem,
  deleteItem,
  forceExpanded,
  onExpandedChange,
  linkResolver,
  onNavigate,
  titleKey,
  titlePrefix,
  subtitleKey,
  accent,
  renderExpandedContent,
  onAskAi,
}: {
  item: Record<string, unknown> & { id: number };
  category: CollectionKey;
  fields: ItemField[];
  updateItem: UpdateItem;
  deleteItem: DeleteItem;
  forceExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  linkResolver?: (
    fieldKey: string,
    value: string,
    item?: Record<string, unknown>
  ) => { tab: string; query: string; label?: string }[];
  onNavigate?: (tab: string, query?: string) => void;
  titleKey?: string;
  titlePrefix?: string;
  subtitleKey?: string;
  accent?: string;
  renderExpandedContent?: (item: Record<string, unknown> & { id: number }) => ReactNode;
  onAskAi?: (
    item: Record<string, unknown> & { id: number },
    category: CollectionKey
  ) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedItem, setEditedItem] = useState(item);
  const [expandedFields, setExpandedFields] = useState<Record<string, boolean>>({});
  const [localExpanded, setLocalExpanded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const { dialogProps, confirm } = useConfirmDialog();
  const isControlled = typeof forceExpanded === 'boolean';
  const isExpanded = isControlled ? forceExpanded : localExpanded;
  const displayItem = isEditing ? editedItem : item;

  const setExpanded = useCallback((next: boolean) => {
    if (isControlled) {
      onExpandedChange?.(next);
    } else {
      setLocalExpanded(next);
    }
  }, [isControlled, onExpandedChange]);

  const handleSave = () => {
    updateItem(category, item.id, editedItem);
    setIsEditing(false);
  };

  const handleStartEditing = () => {
    setEditedItem(item);
    setIsEditing(true);
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete Item',
      message: 'Are you sure you want to delete this item? This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      deleteItem(category, item.id);
    }
  };

  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isEditing) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('button, a, input, textarea, select, [data-prevent-card-click="true"]')) {
      return;
    }
    setExpanded(true);
  };

  const closeExpanded = () => setExpanded(false);

  useEffect(() => {
    if (!isExpanded) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpanded(false);
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    // Scroll card content to top when expanded
    if (cardRef.current) {
      cardRef.current.scrollTop = 0;
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isExpanded, setExpanded]);

  const cardContent = (
      <div
        ref={cardRef}
        className={`p-4 rounded-lg mb-3 card-shell card-clickable${isExpanded ? ' card-expanded' : ''}`}
        data-accent={accent}
        onClick={isExpanded ? undefined : handleCardClick}
      >
        {isExpanded && (
          <button
            type="button"
            onClick={closeExpanded}
            className="icon-button card-modal-close"
            aria-label="Close"
            title="Close"
            data-prevent-card-click="true"
          >
            <X size={14} />
          </button>
        )}
      {isEditing ? (
        <div>
          {fields.map((field) => {
            if (field.readonly) return null;
            return (
              <div key={field.key} className="field-block">
                <label className="field-label">{field.label}</label>
                {field.type === 'textarea' ? (
                  <AutoResizeTextarea
                    value={(editedItem[field.key] as string) || ''}
                    onChange={(e) => setEditedItem({ ...editedItem, [field.key]: e.target.value })}
                    placeholder={field.label}
                    className="input-field textarea-field"
                    minRows={3}
                  />
                ) : field.type === 'select' ? (
                  <>
                    <select
                      value={(editedItem[field.key] as string) || ''}
                      onChange={(e) => setEditedItem({ ...editedItem, [field.key]: e.target.value })}
                      className="input-field"
                    >
                      {field.options?.map((opt) => {
                        const option = typeof opt === 'string' ? { value: opt, label: opt } : opt;
                        return (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        );
                      })}
                    </select>
                    {field.optionDescriptions?.[(editedItem[field.key] as string) || ''] && (
                      <div className="field-meta">
                        <span className="field-hint">
                          {field.optionDescriptions[(editedItem[field.key] as string) || '']}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <input
                    type={field.type || 'text'}
                    value={(editedItem[field.key] as string | number) || ''}
                    onChange={(e) =>
                      setEditedItem({
                        ...editedItem,
                        [field.key]: field.type === 'number' ? parseInt(e.target.value) : e.target.value,
                      })
                    }
                    placeholder={field.label}
                    className="input-field"
                  />
                )}
              </div>
            );
          })}
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleSave}
              className="btn-primary-solid flex items-center gap-1"
            >
              <Save size={14} /> Save
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="text-button text-button-muted"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div>
          {titleKey && (
          <div className="card-header">
              <div>
                <div className="card-title">
                  {titlePrefix
                    ? `${titlePrefix}${String(displayItem[titleKey] || '')}`
                    : String(displayItem[titleKey] || '')}
                </div>
                {subtitleKey && Boolean(displayItem[subtitleKey]) && (
                  <div className="card-subtitle">
                    <span className="meta-chip">{String(displayItem[subtitleKey])}</span>
                  </div>
                )}
              </div>
              <div className="card-toolbar">
                {onAskAi && (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onAskAi(item, category);
                    }}
                    className="icon-button"
                    aria-label="Ask AI"
                    title="Ask AI"
                  >
                    <Sparkles size={14} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleStartEditing}
                  className="icon-button"
                  aria-label="Edit"
                  title="Edit"
                >
                  <Edit2 size={14} />
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="icon-button danger"
                  aria-label="Delete"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          )}

          <div className="card-body">
            {(() => {
              const isSlim = !isExpanded && !isEditing;
              let visibleCount = 0;
              const MAX_SLIM_FIELDS = 2;

              return fields.map((field) => {
                const rawValue = displayItem[field.key];
                const formattedValue = field.format ? field.format(rawValue, displayItem) : undefined;
                const hasRawValue = rawValue !== undefined && rawValue !== null && rawValue !== '';
                const hasFormattedValue =
                  formattedValue !== undefined && formattedValue !== null && String(formattedValue) !== '';
                if ((!hasRawValue && !hasFormattedValue) || field.key === titleKey) return null;

                // Slim mode: only show first 2 visible fields
                if (isSlim) {
                  if (visibleCount >= MAX_SLIM_FIELDS) return null;
                  visibleCount++;
                }

                const renderValue = () => {
                  const optionMap = new Map<string, string>();
                  if (field.type === 'select' && field.options) {
                    for (const option of field.options) {
                      if (typeof option === 'string') {
                        optionMap.set(option, option);
                      } else {
                        optionMap.set(option.value, option.label);
                      }
                    }
                  }

                  const fallbackValue =
                    field.type === 'select' && !hasFormattedValue
                      ? optionMap.get(String(rawValue)) || String(rawValue)
                      : String(rawValue);
                  const value = hasFormattedValue ? String(formattedValue) : fallbackValue;
                  // In slim mode, clamp textareas to 1 line
                  const shouldClampSlim = isSlim && field.type === 'textarea';
                  const shouldClamp =
                    !shouldClampSlim &&
                    field.type === 'textarea' &&
                    value.length > 160 &&
                    !expandedFields[field.key] &&
                    !isExpanded;
                  const useMarkdown = field.type === 'textarea' && !shouldClampSlim;
                  return (
                    <>
                      {useMarkdown && !shouldClamp ? (
                        <MarkdownContent content={value} className="field-value" />
                      ) : (
                        <div className={`field-value ${shouldClampSlim ? 'line-clamp-1' : shouldClamp ? 'line-clamp-4' : ''}`}>{value}</div>
                      )}
                      {!isSlim && field.type === 'textarea' && value.length > 160 && !isExpanded && (
                        <button
                          type="button"
                          className="text-button text-button-muted text-xs mt-1"
                          onClick={() =>
                            setExpandedFields((prev) => ({ ...prev, [field.key]: !prev[field.key] }))
                          }
                        >
                          {expandedFields[field.key] ? 'Show less' : 'Show more'}
                        </button>
                      )}
                      {isExpanded && field.optionDescriptions?.[String(rawValue)] && (
                        <div className="text-xs text-[var(--text-muted)] mt-1">
                          {field.optionDescriptions[String(rawValue)]}
                        </div>
                      )}
                    </>
                  );
                };

                return (
                  <div key={field.key} className="mb-2">
                    <div className="field-label">{field.label}</div>
                    {linkResolver && onNavigate ? (
                      (() => {
                        const linkValue = hasRawValue
                          ? String(rawValue)
                          : String(formattedValue ?? '');
                        const links = linkResolver(field.key, linkValue, displayItem);
                        if (!links || links.length === 0) return renderValue();
                        return (
                          <span className="inline-flex flex-wrap gap-2 mt-1">
                            {links.map((link, idx) => (
                              <button
                                key={`${field.key}-${idx}-${link.query}`}
                                type="button"
                                onClick={() => onNavigate(link.tab, link.query)}
                                className="link-chip"
                              >
                                {link.label || link.query}
                              </button>
                            ))}
                          </span>
                        );
                      })()
                    ) : (
                      renderValue()
                    )}
                  </div>
                );
              });
            })()}
            {isExpanded && renderExpandedContent && renderExpandedContent(item)}
          </div>

          {!titleKey && (
            <div className="card-footer">
              <button
                onClick={handleStartEditing}
                className="icon-button"
                aria-label="Edit"
                title="Edit"
              >
                <Edit2 size={14} />
              </button>
              <button
                onClick={handleDelete}
                className="icon-button danger"
                aria-label="Delete"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <AnimatedCardPortal isExpanded={isExpanded} onClose={closeExpanded}>
        {cardContent}
      </AnimatedCardPortal>
      {createPortal(<ConfirmDialog {...dialogProps} />, document.body)}
    </>
  );
}
